import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { SpacingValue } from "@redotech/redo-web/theme/box";
import { useState } from "react";
import * as reviewCss from "./review.module.css";

export const MerchantOverrideDetailsSection = ({
  overrides,
  pxOverride,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
}) => {
  if (!overrides) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.detailsHeader}
      </Text>
      <RedoToggle
        description={texts.flagReturnDescription}
        disabled={!inputs.enabled}
        label={texts.flagReturn}
        setValue={inputs.onChangeFlagReturn}
        size="sm"
        value={inputs.flagReturn}
      />
      <RedoToggle
        description={texts.manualReviewDescription}
        disabled={!inputs.enabled}
        label={texts.manualReview}
        setValue={inputs.onChangeManualReview}
        size="sm"
        value={inputs.manualReview}
      />
    </Flex>
  );
};

export const MerchantOverrideShippingLabelPayers = ({
  overrides,
}: {
  overrides: MerchantOverrides;
}) => {
  if (!overrides) {
    return null;
  }
  const { texts, inputs, showLabelPayerToggle, showNewOrderLabelPayerToggle } =
    overrides;
  return (
    <Flex flexDirection="column" gap="2xl">
      {showLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysReturnShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysReturnShipping}
          setValue={inputs.onChangeCustomerPaysReturnShipping}
          size="sm"
          value={inputs.customerPaysReturnShipping}
        />
      )}
      {showNewOrderLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysNewOrderShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysNewOrderShipping}
          setValue={inputs.onChangeCustomerPaysNewOrderShipping}
          size="sm"
          value={inputs.customerPaysNewOrderShipping}
        />
      )}
      <Text fontSize="xs" textColor="tertiary">
        {texts.labelDescription}
      </Text>
    </Flex>
  );
};

export const MerchantOverrideDiscountsSection = ({
  overrides,
  pxOverride,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
}) => {
  if (!overrides || !overrides.showDiscountSection) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.discountsHeader}
      </Text>
      <RedoTagInputField
        description={texts.discountInputDescription}
        inputValue={inputs.newDiscountCode}
        label={texts.discountInputLabel}
        setInputValue={inputs.onChangeNewDiscountCode}
        setTags={inputs.onChangeDiscountCodes}
        tags={inputs.discountCodes.map((code) => ({ text: code }))}
      />
      <RedoCheckbox
        disabled={!inputs.enabled}
        label={texts.applyAutomaticDiscountsLabel}
        setValue={inputs.onChangeApplyAutomaticDiscounts}
        size="sm"
        value={inputs.applyAutomaticDiscounts}
      />
    </Flex>
  );
};

enum AdjustmentType {
  FEE = "Fee",
  BONUS = "Bonus",
}

export const MerchantOverrideAdjustmentModal = ({
  overrides,
  isOpen,
  onClose,
  pendingReturnItemId,
}: {
  overrides: NonNullable<MerchantOverrides>;
  isOpen: boolean;
  onClose: () => void;
  pendingReturnItemId: string | undefined;
}) => {
  const { texts, inputs } = overrides;
  const adjustmentOverrideValue =
    inputs.adjustmentOverrides.get(pendingReturnItemId ?? "") ?? 0;
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(
    adjustmentOverrideValue > 0 ? AdjustmentType.BONUS : AdjustmentType.FEE,
  );
  const [inputAmount, setInputAmount] = useState<string>(
    Math.abs(adjustmentOverrideValue).toFixed(2),
  );

  const onApply = () => {
    inputs.onChangeAdjustmentOverride(
      pendingReturnItemId ?? "",
      adjustmentType === AdjustmentType.BONUS
        ? parseFloat(inputAmount)
        : -parseFloat(inputAmount),
    );
    onClose();
  };

  const formatInputAmount = (value: string) => {
    if (!value) {
      return "0.00";
    } else {
      const parsedValue = parseFloat(value);
      return parsedValue > 0 ? parsedValue.toFixed(2) : "0.00";
    }
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: texts.adjustmentModalApplyButton,
        onClick: onApply,
        size: "sm",
      }}
      secondaryButton={{
        text: texts.adjustmentModalCancelButton,
        onClick: onClose,
        size: "sm",
      }}
      title={texts.adjustmentModalTitle}
    >
      <Flex flexDirection="column" gap="3xl">
        <RedoButtonTabGroup<AdjustmentType>
          onChange={(value: AdjustmentType) => setAdjustmentType(value)}
          options={[
            { value: AdjustmentType.FEE, el: <>{AdjustmentType.FEE}</> },
            { value: AdjustmentType.BONUS, el: <>{AdjustmentType.BONUS}</> },
          ]}
          size="sm"
          value={adjustmentType}
        />
        <RedoTextInput
          inputTypeHint="number"
          label={texts.adjustmentModalInputLabel}
          min={0}
          onBlur={() => {
            setInputAmount(formatInputAmount(inputAmount));
          }}
          setValue={(value: string) => setInputAmount(value)}
          step={0.01}
          value={inputAmount}
        />
      </Flex>
    </RedoModal>
  );
};

export const MerchantOverrideItemAdjustments = ({
  overrides,
  pendingReturnItemId,
}: {
  overrides: MerchantOverrides;
  pendingReturnItemId: string | undefined;
}) => {
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  if (!overrides || !pendingReturnItemId) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex flexDirection="column" gap="lg">
      <Flex style={{ width: "155px" }}>
        <RedoButton
          disabled={!inputs.enabled}
          hierarchy="secondary"
          onClick={() => setIsAdjustmentModalOpen(true)}
          size="sm"
          text={texts.adjustFeeButton}
        />
      </Flex>
      <RedoToggle
        description={texts.greenReturnDescription}
        disabled={!inputs.enabled}
        label={texts.greenReturn}
        setValue={(value) =>
          inputs.onChangeGreenReturnOverride(pendingReturnItemId, value)
        }
        size="sm"
        value={inputs.greenReturnOverrides.get(pendingReturnItemId) ?? false}
      />
      {isAdjustmentModalOpen && (
        <MerchantOverrideAdjustmentModal
          isOpen={isAdjustmentModalOpen}
          onClose={() => setIsAdjustmentModalOpen(false)}
          overrides={overrides}
          pendingReturnItemId={pendingReturnItemId}
        />
      )}
    </Flex>
  );
};
